import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import { FC, useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  SelectInput, TextField, TextFieldLabel, Typography
} from 'src/components/UI'
import { SelectInputOption } from 'src/components/UI/CustomUI/atoms/SelectInput/types'
import ExportCSV from 'src/components/UI/CustomUI/molecules/ExportCSV'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import Table from 'src/components/UI/CustomUI/organisms/Table'
import TablePagination from 'src/components/UI/CustomUI/organisms/Table/TablePagination'
import {
  filtersActions, itemsActions, repairItemActions,
  repairListActions,
  territoriesActions
} from 'src/ducks/actions'
import { getRepairlistFilter } from 'src/ducks/filters/selector'
import { RepairListFilterType } from 'src/ducks/filters/types'
import {
  getCsvRepairList,
  getRepairList,
  getRepairListTotal,
  getTerritoriesForDropdown
} from 'src/ducks/selectors'
import { RepairListItem } from 'src/ducks/types'
import {
  formatTimestamp,
  headerCSVParser, ITEM_CATEGORY,
  ROWS_PER_PAGE_SIMPLE
} from 'src/helpers'
import headers from './headers'
import useStyles from './styles'
import { toast } from 'react-toastify'

const itemCategories = Object.values(ITEM_CATEGORY).map(
  (val: string): SelectInputOption => ({
    key: val,
    label: val,
  })
)

itemCategories.unshift({ key: 'all', label: 'All Categories' })

const RepairList: FC = () => {
  const dispatch = useDispatch()

  const styles = useStyles()
  const navigate = useNavigate()

  const repairList = useSelector(getRepairList)
  const csvRepairList = useSelector(getCsvRepairList)
  const total = useSelector(getRepairListTotal)
  const repairListFilter = useSelector(getRepairlistFilter())
  const territories = useSelector(getTerritoriesForDropdown)

  const [pagination, setPagination] = useState({ page: 0, size: 50 })
  const [loading, setLoading] = useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<string>('')
  const [statsToggle] = useState<boolean>(false)
  const [editLaborRateModalOpen, setEditLaborRateModalOpen] = useState<boolean>(false)
  const [editLaborRateTerritory, setEditLaborRateTerritory] = useState<SelectInputOption | null>(null)
  const [editLaborRateCategory, setEditLaborRateCategory] = useState<SelectInputOption | null>(null)
  const [editLaborRate, setEditLaborRate] = useState<string>('')
  /** Solo true después de un intento de envío (click en Edit) con datos inválidos */
  const [editLaborRateShowErrors, setEditLaborRateShowErrors] = useState<boolean>(false)
  const [editLaborRateConfirmOpen, setEditLaborRateConfirmOpen] = useState<boolean>(false)
  const [editLaborRateLoading, setEditLaborRateLoading] = useState<boolean>(false)
  const [search, setSearch] = useState<string>(String(repairListFilter?.search ?? ''))
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const previousSearchRef = useRef<string>('')
  const { category, territory } = repairListFilter || {}

  // CSV
  const downloadRef = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null)
  const headersCSV: any = headerCSVParser(headers)
  const dataCSV = csvRepairList?.reduce((acc: any, curr: any) => {
    const vendor = {
      ...curr,
      name: curr?.title || '',
      lastUsedTimestamp: curr?.lastUsedTimestamp
        ? formatTimestamp(curr?.lastUsedTimestamp, 'MM/DD/YYYY hh:mm a')
        : '',
      win: curr?.wins || '',
      reject: curr?.losses || '',
      used: curr?.usageCount || '',
    }
    acc = [...acc, vendor]
    return acc
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(territoriesActions.fetchTerritories({}, (succ) => { }))
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    const next = String(repairListFilter?.search ?? '')
    setSearch((prev) => (prev === next ? prev : next))
  }, [repairListFilter?.search])

  useEffect(() => {
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
  }, [debouncedSearch])

  const debouncedSearchTrimmed = String(debouncedSearch ?? '').trim()
  const getSearchParams = () => ({
    category: category?.key === 'all' || !category ? null : category?.key,
    territory:
      territory?.key === 'all' || !territory
        ? undefined
        : territory.key,
    search: debouncedSearchTrimmed,
  })

  useEffect(() => {
    if (!editLaborRateModalOpen) {
      setEditLaborRateConfirmOpen(false)
      setEditLaborRateLoading(false)
      return
    }

    const initialTerritory =
      territory && territory?.key !== 'all' ? (territory as SelectInputOption) : null
    const initialCategory =
      category && category?.key !== 'all' ? (category as SelectInputOption) : null

    setEditLaborRateTerritory(initialTerritory)
    setEditLaborRateCategory(initialCategory)
    setEditLaborRate('')
    setEditLaborRateShowErrors(false)
    setEditLaborRateConfirmOpen(false)
    setEditLaborRateLoading(false)
  }, [editLaborRateModalOpen])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Reset to page 0 when search changes to avoid fetching wrong page
    if (debouncedSearchTrimmed !== previousSearchRef.current && pagination.page !== 0) {
      previousSearchRef.current = debouncedSearchTrimmed
      setPagination((prev) => ({ ...prev, page: 0 }))
      return
    }

    previousSearchRef.current = debouncedSearchTrimmed
    setLoading(true)

    dispatch(
      repairListActions.fetchRepairlist(
        {
          ...pagination,
          searchParams: getSearchParams(),
        },
        (succ) => {
          setLoading(false)
        }
      )
    )
  }, [pagination, repairListFilter, debouncedSearchTrimmed])

  const updateRepairListFilter = (newFilter: Partial<RepairListFilterType>) => {
    dispatch(
      filtersActions.setFilters({
        repairList: {
          ...repairListFilter,
          ...newFilter,
        },
      })
    )
  }

  const handleClickRow = (row: RepairListItem): void => {
    navigate(`/repairlist/${row.id}`)
  }

  const handleNewItem = (): void => {
    navigate('/repairlist/new-item')
  }

  const handleCategoryChange = (newValue: SelectInputOption) =>
    updateRepairListFilter({ category: newValue })

  const handleTerritoryChange = (newValue: SelectInputOption) =>
    updateRepairListFilter({ territory: newValue })

  const handleDeleteItem = (id: string): void => {
    setLoading(true)

    dispatch(
      repairItemActions.deleteRepairItem(id, (succ) => {
        if (succ) {
          dispatch(
            repairListActions.fetchRepairlist(
              {
                ...pagination,
                searchParams: getSearchParams(),
              },
              (suc) => {
                setLoading(false)
              }
            )
          )
        } else {
          setLoading(false)
        }
      })
    )
    setDeleteModalOpen(false)
  }

  const handleDownloadCsv = () => {
    const searchParams = {
      ...getSearchParams(),
      search: String(search ?? '').trim(),
    }
    dispatch(
      repairListActions.fetchCsvRepairlist({ searchParams }, (succ: boolean) => {
        if (succ) {
          downloadRef.current?.link.click()
        }
      })
    )
  }

  const isAllOption = (opt: any) => {
    if (!opt) return true
    const key = String(opt?.key ?? '').trim()
    if (key === '' || key === 'all') return true
    if (String(opt?.label ?? '').toLowerCase().includes('all')) return true
    return false
  }
  const isValidLaborRate = (val: string) => {
    const trimmed = String(val ?? '').trim()
    if (!trimmed) return false
    if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return false
    const num = Number(trimmed)
    if (Number.isNaN(num)) return false
    return num >= 0 && num <= 100
  }

  const getLaborRateErrorMessage = (val: string): string => {
    const trimmed = String(val ?? '').trim()
    if (!trimmed) return 'Labor Rate is required'
    if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) {
      return 'Enter a number with up to 2 decimal places'
    }
    const num = Number(trimmed)
    if (Number.isNaN(num)) return 'Enter a valid number'
    if (num < 0 || num > 100) return 'Labor Rate must be between 0 and 100'
    return ''
  }

  const territoryError = editLaborRateShowErrors && isAllOption(editLaborRateTerritory)
  const categoryError = editLaborRateShowErrors && isAllOption(editLaborRateCategory)
  const laborRateInvalid = editLaborRateShowErrors && !isValidLaborRate(editLaborRate)
  const laborRateHelperText = laborRateInvalid ? getLaborRateErrorMessage(editLaborRate) : ''

  const handleOpenEditLaborRate = () => {
    setEditLaborRateModalOpen(true)
  }

  const handleCloseEditLaborRate = () => {
    setEditLaborRateConfirmOpen(false)
    setEditLaborRateModalOpen(false)
  }

  const handleCloseEditLaborRateConfirm = () => {
    setEditLaborRateConfirmOpen(false)
  }

  const handleEditLaborRate = () => {
    const nextTerritoryError = isAllOption(editLaborRateTerritory)
    const nextCategoryError = isAllOption(editLaborRateCategory)
    const nextLaborRateError = !isValidLaborRate(editLaborRate)
    if (nextTerritoryError || nextCategoryError || nextLaborRateError) {
      setEditLaborRateShowErrors(true)
      return
    }

    setEditLaborRateConfirmOpen(true)
  }

  const handleConfirmEditLaborRate = () => {
    setEditLaborRateLoading(true)
    dispatch(itemsActions.editLaborRateByTerritoryAndCategory({
      category: editLaborRateCategory?.key || '',
      territoryId: editLaborRateTerritory?.key || '',
      laborRate: Number(editLaborRate),
    }, (succ, item) => {
      setEditLaborRateLoading(false)
      if (succ && item) {
        toast.success(`Labor rate updated successfully for ${item.itemCount} items in ${editLaborRateTerritory?.label || ''} for ${editLaborRateCategory?.label || ''} category`)
        setEditLaborRateConfirmOpen(false)
        setEditLaborRateModalOpen(false)
        dispatch(repairListActions.fetchRepairlist(
          {
            ...pagination,
            searchParams: getSearchParams(),
          },
        ))
      } else {
        toast.error('Failed to update labor rate for this category and territory')
      }
    }))
  }

  const editLaborRateConfirmMessage = `All ${editLaborRateCategory?.label ?? ''} items for ${editLaborRateTerritory?.label ?? ''} will be updated.  Do you want to proceed?`

  const handleSearch = (value: string) => {
    const next = String(value ?? '')
    setSearch(next)
    updateRepairListFilter({ search: next })
  }

  return (
    <Grid container spacing={2} flexDirection="column">
      <Grid item container className={styles.header}>
        <Typography className={styles.title}>Estimate Price Items</Typography>
        <Box gap={2} display="flex" marginLeft="4px">
          <TextField placeholder="Search Item by Name..." sx={{ width: '250px' }} value={search} onChange={(e) => handleSearch(e.target.value)} />
          <SelectInput
            options={territories}
            value={territory || territories[0]}
            sx={{ width: '270px' }}
            onChange={handleTerritoryChange}
          />
          <SelectInput
            options={itemCategories}
            value={category || itemCategories[0]}
            sx={{ width: '270px' }}
            onChange={handleCategoryChange}
          />
          {/* <Button
            className={
              statsToggle
                ? styles.statsPanelToggleSelected
                : styles.statsPanelToggleUnselected
            }
            onClick={handleStatsToggle}
          >
            {<AssessmentIcon style={{ width: '14px', height: '14px' }} />}
          </Button> */}
          <Button
            className={styles.submitBtn}
            endIcon={<DownloadIcon style={{ width: '16px', height: '16px' }} />}
            onClick={handleDownloadCsv}
          >
            Export CSV
          </Button>
          <ExportCSV
            innerRef={downloadRef}
            headers={headersCSV}
            data={dataCSV}
            fileName="repairlist.csv"
            label=""
          />
          <Button
            className={styles.addBtn}
            onClick={handleNewItem}
            endIcon={<AddIcon style={{ width: '16px', height: '16px' }} />}
          >
            Add New Item
          </Button>
          <Button
            className={styles.addBtn}
            onClick={handleOpenEditLaborRate}
          >
            Edit Labor Rate by Territory and Category
          </Button>
        </Box>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={statsToggle ? 9 : 12}>
          <TablePagination
            callToApi={(start, limit) => {
              setPagination({
                page: start,
                size: limit,
              })
            }}
            totalItems={total}
            rowsPerPageDefault={50}
            rowsPerPageOptions={ROWS_PER_PAGE_SIMPLE}
          />
          <Table
            headers={headers}
            hasPagination={false}
            loading={loading}
            rowAction={handleClickRow}
          >
            {repairList.map((item) => ({
              ...item,
              handleDeleteItem: (id: string) => {
                setDeleteId(id)
                setDeleteModalOpen(true)
              },
            })) || []}
          </Table>
        </Grid>
        {statsToggle && (
          <Grid item xs={3}>
            stats panel
          </Grid>
        )}
      </Grid>
      <Modal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete Repair Item"
      >
        <Grid container flexDirection="column" style={{ gap: '12px' }}>
          <Typography variant="h5">
            Are you sure you wish to delete this price item? This will not
            remove this item in previous estimates.
          </Typography>
          <Grid
            container
            justifyContent="flex-end"
            style={{ gap: '8px', flexWrap: 'nowrap' }}
          >
            <Button
              variant="containedError"
              onClick={() => setDeleteModalOpen(false)}
              sx={{ width: '100%' }}
            >
              Cancel
            </Button>
            <Button
              variant="containedGreen"
              onClick={() => handleDeleteItem(deleteId)}
              sx={{ width: '100%' }}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Modal
        open={editLaborRateModalOpen}
        setOpen={setEditLaborRateModalOpen}
        title="Trade Category Update"
      >
        <Grid container flexDirection="column" style={{ gap: '12px' }}>
          <Box>
            <SelectInput
              options={territories}
              label="Territory"
              value={editLaborRateTerritory || territories?.[0]}
              sx={{ width: '100%' }}
              error={territoryError}
              onChange={(newValue: SelectInputOption) => setEditLaborRateTerritory(newValue)}
            />
            {territoryError && (
              <FormHelperText error>
                Territory is required
              </FormHelperText>
            )}
          </Box>

          <Box>
            <SelectInput
              options={itemCategories}
              label="Category"
              value={editLaborRateCategory || itemCategories?.[0]}
              sx={{ width: '100%' }}
              error={categoryError}
              onChange={(newValue: SelectInputOption) => setEditLaborRateCategory(newValue)}
            />
            {categoryError && (
              <FormHelperText error>
                Category is required
              </FormHelperText>
            )}
          </Box>

          <TextFieldLabel
            labelVariant="h6"
            label="Category Labor Rate"
            placeholder="0.00"
            value={editLaborRate}
            error={laborRateInvalid}
            helperText={laborRateHelperText}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            onChange={(event) => {
              const next = String(event?.target?.value ?? '')
              if (next === '' || /^\d+(\.\d{0,2})?$/.test(next)) {
                setEditLaborRate(next)
              }
            }}
          />

          <Grid
            container
            justifyContent="flex-end"
            style={{ gap: '8px', flexWrap: 'nowrap' }}
          >
            <Button
              variant="containedError"
              onClick={handleCloseEditLaborRate}
              disabled={editLaborRateLoading}
              sx={{ width: '100%' }}
            >
              Cancel
            </Button>
            <Button
              variant="containedGreen"
              onClick={handleEditLaborRate}
              disabled={editLaborRateLoading}
              sx={{ width: '100%' }}
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Modal
        open={editLaborRateConfirmOpen}
        setOpen={setEditLaborRateConfirmOpen}
        title="Confirm labor rate update"
      >
        <Grid container flexDirection="column" style={{ gap: '12px' }}>
          <Typography variant="h5">{editLaborRateConfirmMessage}</Typography>
          <Grid
            container
            justifyContent="flex-end"
            style={{ gap: '8px', flexWrap: 'nowrap' }}
          >
            <Button
              variant="containedError"
              onClick={handleCloseEditLaborRateConfirm}
              disabled={editLaborRateLoading}
              sx={{ width: '100%' }}
            >
              Cancel
            </Button>
            <Button
              variant="containedGreen"
              onClick={handleConfirmEditLaborRate}
              disabled={editLaborRateLoading}
              endIcon={editLaborRateLoading ? <CircularProgress size={16} /> : undefined}
              sx={{ width: '100%' }}
            >
              {editLaborRateLoading ? 'Confirming...' : 'Confirm'}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </Grid>
  )
}

export default RepairList
